import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Multiselect from 'react-select';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from 'react-number-format';
import { parse, format } from 'date-fns';

import MultiSelectCategories from '../MultiSelectCategories';
import ImagesContainer from '../../Shared/ImagesContainer';

import './index.scss';

class AttributesForm extends Component {
  state = {
    categories: this.props.categories,
    expanded: [],
    expandedCategories: [],
    roomPlannerImage: null,
    isMsrpCadAuto: false,
    isMsrpUsdAuto: false,
    isCostCadAuto: false,
    isCostUsdAuto: false,
  };

  inputRefs = {};

  componentDidMount() {
    this.updateDefaultImage(this.props);
    this.determineAutoCalculations();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.productData !== this.props.productData ||
      prevProps.fxCad !== this.props.fxCad
    ) {
      this.determineAutoCalculations();
    }
  }

  determineAutoCalculations = () => {
    const { productData, fxCad } = this.props;
    if (!fxCad) {
      return;
    }

    const usdMsrp = parseFloat(productData.product.usd_price) || 0;
    const cadMsrp = parseFloat(productData.product.cad_price) || 0;
    const usdCost = parseFloat(productData.furniture.cost_usd) || 0;
    const cadCost = parseFloat(productData.furniture.cost_cad) || 0;

    let changes = {};
    let stateChanges = {};

    // Validate existing auto flags for MSRP
    if (this.state.isMsrpCadAuto && Math.abs(cadMsrp - usdMsrp * fxCad) >= 0.01) {
      stateChanges.isMsrpCadAuto = false;
    }
    if (this.state.isMsrpUsdAuto && Math.abs(usdMsrp - cadMsrp / fxCad) >= 0.01) {
      stateChanges.isMsrpUsdAuto = false;
    }

    // Set auto if not set for MSRP
    if (!this.state.isMsrpCadAuto && !this.state.isMsrpUsdAuto) {
      if (cadMsrp === 0 && usdMsrp > 0) {
        changes["product.cad_price"] = (usdMsrp * fxCad).toFixed(2);
        stateChanges.isMsrpCadAuto = true;
      } else if (usdMsrp === 0 && cadMsrp > 0) {
        changes["product.usd_price"] = (cadMsrp / fxCad).toFixed(2);
        stateChanges.isMsrpUsdAuto = true;
      } else if (usdMsrp > 0 && cadMsrp > 0) {
        if (Math.abs(usdMsrp - cadMsrp / fxCad) < 0.01) {
          stateChanges.isMsrpUsdAuto = true;
        }
      }
    }

    // Validate existing auto flags for Cost
    if (this.state.isCostCadAuto && Math.abs(cadCost - usdCost * fxCad) >= 0.01) {
      stateChanges.isCostCadAuto = false;
    }
    if (this.state.isCostUsdAuto && Math.abs(usdCost - cadCost / fxCad) >= 0.01) {
      stateChanges.isCostUsdAuto = false;
    }

    // Set auto if not set for Cost
    if (!this.state.isCostCadAuto && !this.state.isCostUsdAuto) {
      if (cadCost === 0 && usdCost > 0) {
        changes["furniture.cost_cad"] = (usdCost * fxCad).toFixed(2);
        stateChanges.isCostCadAuto = true;
      } else if (usdCost === 0 && cadCost > 0) {
        changes["furniture.cost_usd"] = (cadCost / fxCad).toFixed(2);
        stateChanges.isCostUsdAuto = true;
      } else if (usdCost > 0 && cadCost > 0) {
        if (Math.abs(usdCost - cadCost / fxCad) < 0.01) {
          stateChanges.isCostUsdAuto = true;
        }
      }
    }

    if (Object.keys(changes).length > 0) {
      this.props.onChangeAttribute({
        target: {
          name: "multiple",
          value: changes
        }
      });
    }

    if (Object.keys(stateChanges).length > 0) {
      this.setState(stateChanges);
    }
  };

  getSelectedCategoriesList = (itemsList, itemsIds = []) => {
    if (itemsList !== undefined) {
      return itemsList.filter((item) => itemsIds.some((e) => item.value === e));
    }
    return [];
  };

  updateDefaultImage = (nextProps) => {
    let defaultImage = nextProps.productData.images.image6;
    if (defaultImage) {
      defaultImage = URL.createObjectURL(defaultImage);
    }
    defaultImage = defaultImage || nextProps.productData.product.image6;
    if (this.state.roomPlannerImage !== defaultImage) {
      this.setState({ roomPlannerImage: defaultImage });
    }
  };

  deleteAttribute = (item, i) => {
    if (this.props.onDeleteAttribute) {
      this.props.onDeleteAttribute(i, item.key);
    }
  };

  addAttribute = () => {
    if (this.props.onAddAttribute) {
      this.props.onAddAttribute();
    }
  };

  changeAttributes = (e) => {
    this.props.onChangeAttribute(e);
  };

  handleNumberValueChange = (values, name, hasPrefix = false) => {
    const { fxCad, productData } = this.props;
    let newValue = values.value !== undefined ? values.value : (values.floatValue !== undefined ? values.floatValue.toString() : '');
    if (hasPrefix && newValue && typeof newValue === 'string' && !newValue.startsWith('$')) {
      newValue = '$ ' + newValue;
    }
    if (newValue === null || newValue === undefined) {
      newValue = '';
    }

    const parsedValue = parseFloat(values.floatValue) || 0;
    let stateChanges = {};
    let changes = { [name]: parsedValue.toFixed(2) };
    let current = 0;

    if (name === "product.usd_price") {
      current = parseFloat(productData.product.usd_price) || 0;
      if (parsedValue !== current) {
        if (this.state.isMsrpCadAuto) {
          changes["product.cad_price"] = (parsedValue * fxCad).toFixed(2);
        }
        stateChanges.isMsrpUsdAuto = false;
      }
    } else if (name === "product.cad_price") {
      current = parseFloat(productData.product.cad_price) || 0;
      if (parsedValue !== current) {
        if (this.state.isMsrpUsdAuto) {
          changes["product.usd_price"] = (parsedValue / fxCad).toFixed(2);
        }
        stateChanges.isMsrpCadAuto = false;
      }
    } else if (name === "furniture.cost_usd") {
      current = parseFloat(productData.furniture.cost_usd) || 0;
      if (parsedValue !== current) {
        if (this.state.isCostCadAuto) {
          changes["furniture.cost_cad"] = (parsedValue * fxCad).toFixed(2);
        }
        stateChanges.isCostUsdAuto = false;
      }
    } else if (name === "furniture.cost_cad") {
      current = parseFloat(productData.furniture.cost_cad) || 0;
      if (parsedValue !== current) {
        if (this.state.isCostUsdAuto) {
          changes["furniture.cost_usd"] = (parsedValue / fxCad).toFixed(2);
        }
        stateChanges.isCostCadAuto = false;
      }
    } else {
      this.props.onChangeAttribute({ target: { name, value: newValue } });
      return;
    }

    this.props.onChangeAttribute({
      target: {
        name: Object.keys(changes).length > 1 ? "multiple" : name,
        value: Object.keys(changes).length > 1 ? changes : parsedValue.toFixed(2)
      }
    });

    if (Object.keys(stateChanges).length > 0) {
      this.setState(stateChanges);
    }
  };

  changeDiscontinued = (e) => {
    if (this.props.onDiscontinuedChange) {
      this.props.onDiscontinuedChange(e);
    }
  };

  changeOtherAttribute = (e) => {
    if (this.props.onChangeOtherAttribute) {
      this.props.onChangeOtherAttribute(e.target);
    }
  };

  getValues = (data, firstPath, secondPath) => {
    const values = [];
    const { productData } = this.props;
    if (typeof secondPath === 'undefined') {
      productData[firstPath].forEach((id) => values.push(data.filter((e) => e.value === id || e.value === id)));
    } else {
      productData[firstPath][secondPath].forEach((id) => values.push(data.filter((e) => e.value === id || e.value === id.id)));
    }
    return values.map((e) => e[0]);
  };

  handlePlannerImageChange = (file, url) => {
    const fileData = file || '';
    if (this.props.onChangeRoomPlannerImage) {
      this.props.onChangeRoomPlannerImage(fileData, url);
    }
  };

  validate = (value) => {
    const isNotValid = this.props.missingRequiredFields && (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0));
    return isNotValid ? 'is-invalid' : '';
  };

  getRoomPlannerImage = (data) => {
    const url = data.product.url_image6;
    const file = data.product.image6;
    const isFile = file.url || file.type;
    return isFile ? [this.getImageData(file)] : [url];
  };

  getImageData = (file) => (file.type ? URL.createObjectURL(file) : file.url);

  onCategoryChange = (categories) => {
    if (this.props.onCategoryChange) {
      this.props.onCategoryChange(categories);
    }
  };

  parseEtaDate = (etaDate) => {
    if (!etaDate) return '';
    const formats = ['M/d/yyyy', 'dd-MMM-yyyy'];
    for (const fmt of formats) {
      try {
        const parsedDate = parse(etaDate, fmt, new Date());
        if (!isNaN(parsedDate)) {
          return format(parsedDate, 'yyyy-MM-dd');
        }
      } catch (error) {}
    }
    console.error('Error parsing eta_date:', etaDate);
    return '';
  };

  render() {
    const {
      productData,
      categories,
      colors,
      features,
      materials,
      suppliers,
      onRoomVariantLinkClick,
    } = this.props;

    const colorsValues = this.getValues(colors, 'furniture', 'color_ids');
    const materialsValues = this.getValues(materials, 'furniture', 'material_ids');
    const featureValues = this.getSelectedCategoriesList(features, productData.feature_tag_ids);
    const supplierValue = suppliers.filter((e) => e.value === productData.product.supplier_id);

    return (
      <form className="container-fluid attributes-form pl-3 pl-lg-5">
        <div className="row pl-3 pl-lg-5">
          <div className="col-xl-8 order-xl-1 order-2 pl-0 pl-lg-5">
            <div className="row">
              <div className="col-12 col-md-3">
                <p>NAME: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-9">
                <input
                  className={`form-control ${this.validate(productData.product.name)}`}
                  name="product.name"
                  value={productData.product.name || ''}
                  onChange={(e) => this.changeAttributes(e)}
                />
                <div className="invalid-feedback">Please choose a name.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>DESCRIPTION:</p>
              </div>
              <div className="col-12 col-md-9 attributes-form__product_description">
                <textarea
                  name="product.description"
                  value={productData.product.description || ''}
                  onChange={(e) => this.changeAttributes(e)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3 attributes-form__categories">
                <p>CATEGORIES: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-9">
                <div className={`${this.validate(productData.product_category_ids)}`}>
                  <MultiSelectCategories
                    product_id={productData.product.id}
                    categories={categories}
                    selected_categories={productData.product_category_ids}
                    onCategoryChange={this.onCategoryChange}
                    getSelectedCategoriesList={this.getSelectedCategoriesList.bind(this)}
                    classNamePrefix="select-categories"
                  />
                </div>
                <div className="invalid-feedback">Please choose a category.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>SKU: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-9">
                <input
                  className={`form-control ${this.validate(productData.product.sku)}`}
                  name="product.sku"
                  value={productData.product.sku || ''}
                  onChange={(e) => this.changeAttributes(e)}
                />
                <div className="invalid-feedback">Please choose a sku.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>SUPPLIER: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-9">
                <Select
                  className={`select2 select2-single ${this.validate(supplierValue)}`}
                  value={supplierValue}
                  options={suppliers}
                  onChange={this.props.onSupplierChange}
                />
                <input className={`form-control invisible-input ${this.validate(supplierValue)}`} />
                <div className="invalid-feedback">Please choose a supplier.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>COLORS:</p>
              </div>
              <div className="col-12 col-md-9">
                <Multiselect
                  isMulti
                  className="select2"
                  value={colorsValues}
                  options={colors}
                  onChange={this.props.onColorsChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>MATERIALS:</p>
              </div>
              <div className="col-12 col-md-9">
                <Multiselect
                  isMulti
                  className="select2"
                  value={materialsValues}
                  options={materials}
                  onChange={this.props.onMaterialsChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-3">
                <p>FEATURES:</p>
              </div>
              <div className="col-12 col-md-9">
                <Multiselect
                  isMulti
                  className="select2"
                  value={featureValues}
                  options={features}
                  onChange={this.props.onFeaturesChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-6 col-lg-3 d-flex flex-column">
                <p className="attributes-form__label" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', lineHeight: this.state.isMsrpCadAuto ? '25px' : '42px' }}>
                  MSRP CAD$:
                  {this.state.isMsrpCadAuto && (
                    <span className="attributes-form__fx-note" style={{ display: 'block', fontSize: '14px', marginTop: '2px', marginBottom: 0, color: '#6c757d' }}>
                      (based on current Fx rate)
                    </span>
                  )}
                </p>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <NumberFormat
                  prefix="$ "
                  name="product.cad_price"
                  value={productData.product.cad_price || ''}
                  onValueChange={(values) => this.handleNumberValueChange(values, "product.cad_price", true)}
                  getInputRef={(el) => (this.inputRefs["product.cad_price"] = el)}
                  allowEmptyFormatting={true}
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-3 d-flex flex-column">
                <p className="attributes-form__label" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', lineHeight: this.state.isMsrpUsdAuto ? '25px' : '42px' }}>
                  <span>MSRP USD$: <span className="required-asterisk"> *</span></span>
                  {this.state.isMsrpUsdAuto && (
                    <span className="attributes-form__fx-note" style={{ display: 'block', fontSize: '14px', marginTop: '2px', marginBottom: 0, color: '#6c757d' }}>
                      (based on current Fx rate)
                    </span>
                  )}
                </p>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <NumberFormat
                  prefix="$ "
                  name="product.usd_price"
                  value={productData.product.usd_price || ''}
                  onValueChange={(values) => this.handleNumberValueChange(values, "product.usd_price", true)}
                  getInputRef={(el) => (this.inputRefs["product.usd_price"] = el)}
                  allowEmptyFormatting={true}
                  className={`form-control ${this.validate(productData.product.usd_price)}`}
                />
                <div className="invalid-feedback">Please enter a valid MSRP USD.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-6 col-lg-3 d-flex flex-column">
                <div className="attributes-form__label" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', lineHeight: this.state.isCostCadAuto ? '25px' : '42px' }}>
                  <span>COST CAD$:</span>
                  {this.state.isCostCadAuto && (
                    <span className="attributes-form__fx-note" style={{ display: 'block', fontSize: '14px', marginTop: '2px', marginBottom: 0, color: '#6c757d' }}>
                      (based on current Fx rate)
                    </span>
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <NumberFormat
                  prefix="$ "
                  name="furniture.cost_cad"
                  value={productData.furniture.cost_cad || ''}
                  onValueChange={(values) => this.handleNumberValueChange(values, "furniture.cost_cad", true)}
                  getInputRef={(el) => (this.inputRefs["furniture.cost_cad"] = el)}
                  allowEmptyFormatting={true}
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-3 d-flex flex-column">
                <p className="attributes-form__label" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', lineHeight: this.state.isCostUsdAuto ? '25px' : '42px' }}>
                  <span>COST USD$: <span className="required-asterisk"> *</span></span>
                  {this.state.isCostUsdAuto && (
                    <span className="attributes-form__fx-note" style={{ display: 'block', fontSize: '14px', marginTop: '2px', marginBottom: 0, color: '#6c757d' }}>
                        (based on current Fx rate)
                      </span>
                  )}
                </p>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <NumberFormat
                  prefix="$ "
                  name="furniture.cost_usd"
                  value={productData.furniture.cost_usd || ''}
                  onValueChange={(values) => this.handleNumberValueChange(values, "furniture.cost_usd", true)}
                  getInputRef={(el) => (this.inputRefs["furniture.cost_usd"] = el)}
                  allowEmptyFormatting={true}
                  className={`form-control ${this.validate(productData.furniture.cost_usd)}`}
                />
                <div className="invalid-feedback">Please enter a valid Cost USD.</div>
              </div>
            </div>
            <div className="row mb-5 mb-sm-0">
              <div className="col-12 col-sm-6 col-lg-3">
                <p>IS ASSEMBLY REQUIRED: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-sm-6 col-lg-9">
                <select
                  className={`form-control ${this.validate(productData.product.is_assembly_required)}`}
                  name="product.is_assembly_required"
                  value={productData.product.is_assembly_required}
                  onChange={(e) => this.changeAttributes(e)}
                >
                  <option value="yes">Yes</option>
                  <option value="yes_light_assembly">Yes - Light Assembly</option>
                  <option value="yes_kd">Yes - KD</option>
                  <option value="no">No</option>
                  <option value="need_to_check">Need to Check</option>
                </select>
                <div className="invalid-feedback">Please select whether assembly is required.</div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <p>WIDTH: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="product.width"
                  value={productData.product.width}
                  onChange={(e) => this.changeAttributes(e)}
                  className={`form-control ${this.validate(productData.product.width)}`}
                />
                <div className="invalid-feedback">Please enter a valid width.</div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <p>SEAT WIDTH:</p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="furniture.seat_width"
                  value={productData.furniture.seat_width}
                  onChange={(e) => this.changeAttributes(e)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <p>HEIGHT: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="product.height"
                  value={productData.product.height}
                  onChange={(e) => this.changeAttributes(e)}
                  className={`form-control ${this.validate(productData.product.height)}`}
                />
                <div className="invalid-feedback">Please enter a valid height.</div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <p>SEAT HEIGHT:</p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="furniture.seat_height"
                  value={productData.furniture.seat_height}
                  onChange={(e) => this.changeAttributes(e)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <p>DEPTH: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="product.depth"
                  value={productData.product.depth}
                  onChange={(e) => this.changeAttributes(e)}
                  className={`form-control ${this.validate(productData.product.depth)}`}
                />
                <div className="invalid-feedback">Please enter a valid depth.</div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <p>SEAT DEPTH:</p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="furniture.seat_depth"
                  value={productData.furniture.seat_depth}
                  onChange={(e) => this.changeAttributes(e)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <p>WEIGHT: <span className="required-asterisk">*</span></p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="furniture.weight"
                  value={productData.furniture.weight}
                  onChange={(e) => this.changeAttributes(e)}
                  className={`form-control ${this.validate(productData.furniture.weight)}`}
                />
                <div className="invalid-feedback">Please enter a valid weight.</div>
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <p>ARM HEIGHT:</p>
              </div>
              <div className="col-12 col-md-6 col-lg-2">
                <input
                  name="furniture.arm_height"
                  value={productData.furniture.arm_height}
                  onChange={(e) => this.changeAttributes(e)}
                />
              </div>
            </div>
          </div>
          <div className="col-xl-4 order-xl-2 order-1">
            <div className="row justify-content-center">
              <div className="col-12 col-sm-8 col-xl-12 text-center">
                <p>ROOM PLANNER IMAGE:</p>
              </div>
              <div className="col-12 col-md-6">
                <ImagesContainer
                  images={this.getRoomPlannerImage(productData)}
                  onImagesChange={(file, url) => this.handlePlannerImageChange(file, url)}
                  imageSize="small"
                  customStyles="product"
                />
              </div>
            </div>
            <div className="row justify-content-center p-1">
              <div className="col-12 col-sm-8 col-xl-12 text-center">
                <p>PRODUCT STATUS:</p>
              </div>
              <div className="row justify-content-center w-100">
                <div className="col-6 col-md-6 attributes-form__label">
                  <p>TOTAL STOCK:</p>
                </div>
                <div className="col-6 col-md-6 attributes-form__input">
                  <NumberFormat
                    name="product.stock_number"
                    value={productData.product.stock_number || ''}
                    onValueChange={(values) => this.handleNumberValueChange(values, "product.stock_number")}
                    getInputRef={(el) => (this.inputRefs["product.stock_number"] = el)}
                    allowEmptyFormatting={true}
                  />
                  <div className="invalid-feedback">Please choose a correct stock number.</div>
                </div>
              </div>
              {productData?.product?.inventories?.length > 0 &&
                productData.product.inventories.map((inv) => (
                  <div className="row justify-content-center w-100" key={inv.label}>
                    <div className="col-6 col-md-6 attributes-form__label">
                      <span>{inv.label}:</span>
                    </div>
                    <div className="col-6 col-md-6 attributes-form__input">
                      <NumberFormat
                        name="furniture.inventory_count"
                        value={inv.value || ''}
                        onValueChange={(values) => this.handleNumberValueChange(values, "furniture.inventory_count")}
                        getInputRef={(el) => (this.inputRefs["furniture.inventory_count"] = el)}
                        allowEmptyFormatting={true}
                      />
                      <input className="form-control invisible-input" />
                    </div>
                  </div>
                ))}
              <>
                <div className="row justify-content-center w-100">
                  <div className="col-6 col-md-6 attributes-form__label">
                    <p>ETA:</p>
                  </div>
                  <div className="col-6 col-md-6 attributes-form__input">
                    <input
                      type="date"
                      className="form-control"
                      name="product.eta_date"
                      value={this.parseEtaDate(productData.product.eta_date)}
                      onChange={(e) => this.changeAttributes(e)}
                    />
                  </div>
                </div>
                <div className="row justify-content-center w-100">
                  <div className="col-6 col-md-6 attributes-form__label">
                    <p>ETA QTY:</p>
                  </div>
                  <div className="col-6 col-md-6 attributes-form__input">
                    <NumberFormat
                      name="product.eta_qty"
                      value={productData.product.eta_qty || ''}
                      onValueChange={(values) => this.handleNumberValueChange(values, "product.eta_qty")}
                      getInputRef={(el) => (this.inputRefs["product.eta_qty"] = el)}
                      allowEmptyFormatting={true}
                    />
                    <div className="invalid-feedback">Please enter a valid ETA quantity.</div>
                  </div>
                </div>
              </>
              <div className="row justify-content-center w-100">
                <div className="col-6 col-md-6 attributes-form__label">
                  <p>DISCONTINUED:</p>
                </div>
                <div className="col-6 col-md-6 attributes-form__input">
                  <Select
                    className="select2 select2-single"
                    classNamePrefix="select-discontinued"
                    value={{ label: productData.product.is_discontinued ? 'Yes' : 'No', value: productData.product.is_discontinued }}
                    options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                    onChange={({ value }) => this.changeDiscontinued(value)}
                  />
                  <input className="form-control invisible-input" />
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-8 col-xl-12 text-center">
              <p>ROOM VARIANTS ({productData?.room_variants?.length || 0}):</p>
            </div>
            <div className="row justify-content-center p-1 attributes-form__product-used">
              {productData?.room_variants?.length ? (
                <div className="column justify-content-center w-100 attributes-form__product-used-list">
                  {productData.room_variants?.map(
                    ({ room_id, variant_id, variant_sku, updated_at }) => (
                      <div
                        key={variant_sku}
                        className="col-xl-12 attributes-form__link-container"
                        aria-hidden
                        onClick={() => onRoomVariantLinkClick(room_id, variant_id)}
                      >
                        <div className="attributes-form__link">{variant_sku}</div>
                        <div className="attributes-form__link-date">{updated_at.slice(0, 10)}</div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="col-12 col-sm-8 col-xl-12 text-center">
                  <p>No room variants</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row justify-content-start">
          <div className="col-10 col-md-7">
            <div className="row justify-content-start pl-0 pl-lg-5">
              <div className="col-8 col-md-10 col-lg-6 pl-3 pl-lg-5">
                <p>OTHER ATTRIBUTES:</p>
              </div>
              <div className="col-2 custom-attribute-add" aria-hidden onClick={this.addAttribute.bind(this)}>
                <FontAwesomeIcon icon={faPlus} />
              </div>
            </div>
          </div>
        </div>
        {productData.product.product_attributes_array.map((item, i) => (
          <div key={i} className="row pl-3 pl-lg-4">
            <div className="col-12 col-sm-3 col-lg-1 offset-0 offset-lg-1 mr-0 mr-lg-4 pl-0 pl-md-5">
              <p>NAME:</p>
            </div>
            <div className="col-12 col-sm-7 col-lg-3 mr-0 mr-lg-5">
              <input
                onChange={this.changeOtherAttribute.bind(this)}
                name={`name :${i}`}
                value={item.key || ''}
              />
            </div>
            <div className="col-12 col-sm-3 col-lg-1 mr-0 mr-lg-4 pl-0 pl-md-5">
              <p>VALUE:</p>
            </div>
            <div className="col-9 col-sm-5 col-sm-7 col-lg-3">
              <input
                onChange={this.changeOtherAttribute.bind(this)}
                name={`value:${i}`}
                value={item.value || ''}
              />
            </div>
            <div
              className="col-1 col-md-1 custom-attribute-remove"
              aria-hidden
              onClick={this.deleteAttribute.bind(this, item, i)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </div>
          </div>
        ))}
      </form>
    );
  }
}

AttributesForm.propTypes = {
  categories: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
  features: PropTypes.array,
  materials: PropTypes.array.isRequired,
  missingRequiredFields: PropTypes.bool.isRequired,
  onAddAttribute: PropTypes.func.isRequired,
  onChangeAttribute: PropTypes.func.isRequired,
  onChangeOtherAttribute: PropTypes.func.isRequired,
  onChangeRoomPlannerImage: PropTypes.func.isRequired,
  onColorsChange: PropTypes.func.isRequired,
  onFeaturesChange: PropTypes.func,
  onDeleteAttribute: PropTypes.func.isRequired,
  onDiscontinuedChange: PropTypes.func,
  onMaterialsChange: PropTypes.func.isRequired,
  onSupplierChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  productData: PropTypes.object.isRequired,
  suppliers: PropTypes.array.isRequired,
  fxCad: PropTypes.number,
};

export default AttributesForm;
